"use strict";

/**
 * Bezeichnet eine Gruppe von Layern, entweder in der Form eines
 * {@link gc.gis.map.GCLayerTreeManager.LayerListNode|Arrays von Layern} oder
 * in Form eines {@link gc.gis.map.GCLayerTreeManager.GroupNode|Gruppenlayers}.
 *
 * @abstract
 * @typedef gc.gis.map.GCLayerTreeManager.TreeNode
 * @property {boolean} __changed
 *   Indikator, dass sich dieser Baum beim Vergleich mit einem anderen Baum an
 *   dieser Stelle geändert hat. Privat im Sinne, dass nur der Layermanager
 *   diese Eigenschaft auswertet.
 */

/**
 * Liste von zusammenfassbaren Layern.
 * Erbt von {@link gc.gis.map.GCLayerTreeManager.TreeNode}.
 *
 * @typedef gc.gis.map.GCLayerTreeManager.LayerListNode
 * @extents gc.gis.map.GCLayerTreeManager.TreeNode
 *
 * @property {gc.gis.layer.GCAbstractLayer[]} layers
 *   Layer, die zusammengefasst werden können.
 */

/**
 * Gruppenlayer, der gesondert behandelt wird, weil er halbtransparent ist.
 * Erbt von {@link gc.gis.map.GCLayerTreeManager.TreeNode}.
 *
 * @typedef gc.gis.map.GCLayerTreeManager.GroupNode
 * @extents gc.gis.map.GCLayerTreeManager.TreeNode
 *
 * @property {gc.gis.layer.GCGroupLayer} _ref
 *   Referenz auf den eigentlichen GC-Layer.
 * @property {gc.gis.map.GCLayerTreeManager.TreeNode[]} groups
 *   Liste von zusammenfassbaren Gruppen.
 * @property {number} opacity
 *   Deckkraft des Gruppenlayers.
 */

/**
 * @classdesc
 * Klasse, die den Layerbaum analysiert und WMS-Layer nach Möglichkeit
 * zusammenfasst, um GetMap-Requests einzusparen.
 * <h4>Aktualisierung des Baums</h4>
 * <p>Der Baum wird aktualisiert, wenn:</p>
 * <ul class="list">
 * <li>sich die Sichtbarkeit eines Layers <em>signifikant</em><strong>*</strong>
 *   ändert</li>
 * <li>sich die Transparenz eines Layers von <code>1</code> auf kleiner
 *   <code>1</code> ändert<strong>*</strong></li>
 * <li>sich die Transparenz eines Layers von kleiner <code>1</code> auf
 *   <code>1</code> ändert<strong>*</strong></li>
 * <li>sich die Transparenz eines Layers von <code>0</code> auf größer
 *   <code>0</code> ändert<strong>*</strong></li>
 * <li>sich die Transparenz eines Layers von größer <code>0</code> auf
 *   <code>0</code> ändert<strong>*</strong></li>
 * <li>ein Layer hinzugefügt wird</li>
 * <li>ein Layer entfernt wird</li>
 * </ul>
 * <p><strong>*)</strong> ist ein übergeordneter Layer, und damit auch seine
 *   Unterlayer, nicht sichtbar, bleibt der Baum erhalten.</p>
 *
 * <h4>Layerzusammenfassung</h4>
 * <p>Layer können zusammengefasst werden, wenn:</p>
 * <ul class="list">
 * <li>sie Raster-Layer sind</li>
 * <li>ihre {@link gc.gis.layer.GCLayer#getUrl|baseUrl} übereinstimmt</li>
 * <li>sie nicht {@link gc.gis.layer.GCRasterLayer#isCached|gecacht} gecacht
 *   werden</li>
 * <li>sie alle entweder {@link gc.gis.layer.GCRasterLayer#isTiled|tiled} oder
 *   nicht <code>tiled</code> sind</li>
 * <li>sie sichtbar sind</li>
 * <li>ihre {@link gc.gis.layer.GCAbstractLayer#getOpacity|Deckkraft}
 *   <code>100%</code> beträgt</li>
 * </ul>
 *
 * <h4>Vorgehensweise</h4>
 * <ol class="list">
 * <li>"möglichst flache" GCLayer-Liste erstellen</li>
 * <li>diese flache Liste mit alter vergleichen: hat sich was geändert? Wenn ja:
 *   <ol class="list">
 *   <li>die betreffenden Stellen werden mit <code>__changed=true</code> markiert (außer Vektorlayer!)</li>
 *   <li>Openlayers-Layer aus Openlayers-Karte entfernen</li>
 *   <li>Openlayers-Baum neubauen: da, wo <code>__changed===true</code> neue <code>ol.Layer</code>-Instanzen erstellen, ansonsten existierende <code>ol.Layer</code>-Instanzen verwenden</li>
 *   <li>Openlayers-Layer der Openlayers-Karte hinzufügen</li>
 *   </ol>
 * </li>
 * </ol>
 *
 * @class
 * @extends gc.gis.GCObservable
 * @memberof gc.gis
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen
 * @param {gc.gis.map.GCMapView} options.map Referenz auf Karte
 *
 * @see gc.gis.map.GCAbstractLayerFactory
 */
gc.gis.map.GCLayerTreeManager = function( options )
{
	gc.gis.GCObservable.call( this );

	/**
	 * Zu managende Karte.
	 *
	 * @protected
	 * @type {gc.gis.map.GCMapView}
	 */
	this._map             = options.map;
	/**
	 * Timeout, ab wann der Layerbaum neu gebaut wird.
	 *
	 * @private
	 * @type {number}
	 */
	this.__update_timeout = this._map.getLoadingTimeout();
	/**
	 * Erstellt Kartenbibliotheksspezifische Layerinstanzen.
	 *
	 * @private
	 * @type {gc.gis.map.GCAbstractLayerFactory}
	 */
	this.__factory        = this._map.getMap().getLayerFactory();
	/**
	 * Zuletzt gebauter Layertree. Dient Vergleichszwecken in
	 * {@link gc.gis.map.GCLayerTreeManager#__treeHasChanged}.
	 *
	 * @private
	 * @type {undefined}
	 * @see {gc.gis.map.GCLayerTreeManager#__treeHasChanged}
	 */
	this.__last_tree      = undefined;
	/**
	 * Timer-Objekt für den Rebuild.
	 *
	 * @private
	 * @type {undefined}
	 */
	this.__timer          = undefined;

	this.__initEvents();
};
gc.gis.map.GCLayerTreeManager.prototype = Object.create( gc.gis.GCObservable.prototype );

/**
 * Initialisiert Layer-Events am Kartenobjekt.
 *
 * @private
 * @method
 */
gc.gis.map.GCLayerTreeManager.prototype.__initEvents = function()
{
	var self = this;

	this._map.on( gc.gis.map.GCMapEvent.LAYER_ADDED, this, function(evt) {
		for( var i=0; i<evt.layers.length; i++ )
		{
			self._registerLayer( evt.layers[i] );
		}
		self.update();
	});
	this._map.on( gc.gis.map.GCMapEvent.LAYER_REMOVED, this, function(evt) {
		for( var i=0; i<evt.layers.length; i++ )
		{
			self._unregisterLayer( evt.layers[i] );
		}
		self.update();
	});
	this._map.on( gc.gis.map.GCMapEvent.LAYER_MOVED, this, function(evt) {
		self.update();
	});
};

/**
 * Registriert Sichtbarkeitsevents an Layerobjekten.
 *
 * @protected
 * @param {gc.gis.layer.GCAbstractLayer} layer
 */
gc.gis.map.GCLayerTreeManager.prototype._unregisterLayer = function( layer )
{
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager._unregisterLayer: "'+ layer.getLabel() +'"');
	layer.un( gc.gis.layer.GCLayerEvent.OPACITY_CHANGED, this );
	layer.un( gc.gis.layer.GCLayerEvent.VISIBILITY_CHANGED, this );
};

/**
 * @protected
 * @param {gc.gis.layer.GCAbstractLayer} layer
 */
gc.gis.map.GCLayerTreeManager.prototype._registerLayer = function( layer )
{
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager._registerLayer: "'+ layer.getLabel() +'"');
	var self = this;

	layer.on( gc.gis.layer.GCLayerEvent.OPACITY_CHANGED, self, function(evt ) {
		//gc.gis.iface.log.debug( self, 'GCLayerTreeManager.OPACITY_CHANGED: old='+ evt.oldValue +'  new='+ evt.newValue );
		if( (evt.oldValue % 1 === 0 && evt.newValue % 1 !== 0)
		 || (evt.oldValue % 1 !== 0 && evt.newValue % 1 === 0 ) )
		{
			self.update();
		}
		else if( evt.layer.getImpl() )
		{
			//gc.gis.iface.log.debug( self, 'GCLayerTreeManager.OPACITY_CHANGED: adjusting ... ');
			evt.layer.getImpl().setOpacity( evt.newValue );	// FIXME: library dependant
		}
	});
	layer.on( gc.gis.layer.GCLayerEvent.VISIBILITY_CHANGED, self, function(evt ) {
		self.update();
	});
};

/**
 * @public
 */
gc.gis.map.GCLayerTreeManager.prototype.update = function()
{
	var self = this;

	if( this.__timer )
	{
		clearTimeout( this.__timer );
		// gc.gis.iface.log.debug( this, 'GCLayerTreeManager: Update cancelled ... ');
		this._fireEvent( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_CANCELLED, {
			forced: false
		});
	}

	this.__timer = setTimeout( function() {
		try
		{
			self.__doUpdate();
		}
		catch( err )
		{
			gc.gis.iface.log.error( self, err );
		}
		finally
		{
			self.__timer = undefined;
		}
	}, this.__update_timeout );

	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager: Update scheduled ... ');
	this._fireEvent( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_SCHEDULED, {
		timeout: this.__update_timeout
	});
};

/**
 * @public
 */
gc.gis.map.GCLayerTreeManager.prototype.forceUpdate = function()
{
	if( this.__timer )
	{
		clearTimeout( this.__timer );
		// gc.gis.iface.log.debug( this, 'GCLayerTreeManager: Update cancelled ... ');
		this._fireEvent( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_CANCELLED, {
			forced: true
		});
	}
	this.__last_tree = undefined;
	this.__doUpdate();
};

/**
 * @private
 */
gc.gis.map.GCLayerTreeManager.prototype.__doUpdate = function()
{
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__doUpdate()');

	var gc_layers = this._map.getLayers();

	var new_tree = this.__createLayerGroups( gc_layers );
	// console.log( new_tree );
	if( this.__treeHasChanged( new_tree, this.__last_tree ) )
	{
		// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__doUpdate(): updating ... ');
		// console.log( new_tree );
		this.__rebuildMapLayers( new_tree );
		this.__last_tree = new_tree;
	}
	else
	{
		// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__doUpdate(): nothing changed.');
	}

	this._fireEvent( gc.gis.map.GCMapEvent.LAYER_TREE_UPDATE_FINISHED, {
		tree: new_tree
	});
};

/**
 * @private
 */
gc.gis.map.GCLayerTreeManager.prototype.__rebuildMapLayers = function( tree )
{
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__rebuildMapLayers(): Entering ... ');

	//console.log(tree);

	// Openlayers-Layer aus Openlayers-Karte entfernen
	this._map.getMap().removeAllLayers();

	for( var i=0; i<tree.length; i++ )
	{
		// ol.Layer-Instanz
		var impl_layer;

		// Unterbaum hat sich geändert -> neue Openlayers-Instanz(en) erstellen
		if( tree[i].__changed )
		{
			// Flache Layerliste
			if( tree[i].layers )
			{
				// console.log('GCLayerTreeManager.__rebuildMapLayers():   creating .layers__changed:');
				// console.log( tree[i].layers );

				impl_layer = this.__factory.createMergedLayer( tree[i].layers );
			}
			// Grouplayer
			else if( tree[i].groups )
			{
				// console.log('GCLayerTreeManager.__rebuildMapLayers():   creating .groups__changed:');
				// console.log( tree[i] );

				impl_layer = this.__factory.createGroupLayer( tree[i] );
			}
		}
		// Unterbaum hat sich nicht geändert
		else
		{
			// Layerarray mergen
			if( tree[i].layers )
			{
				// console.log('GCLayerTreeManager.__rebuildMapLayers():   creating .layers:');
				// console.log( tree[i].layers );

				// Es existiert noch keine Referenz
				if( tree[i].layers.length > 0 && !tree[i].layers[0].getImpl() )
				{
					impl_layer = this.__factory.createMergedLayer( tree[i].layers );
				}
				// Es existiert eine Referenz, also weiterverwenden
				else
				{
					impl_layer = tree[i].layers[0].getImpl();
				}
			}
			// Gruppenlayer behandeln
			else if( tree[i].groups )
			{
				// console.log('GCLayerTreeManager.__rebuildMapLayers():   creating .groups:');
				// console.log( tree[i] );

				// Es existiert noch keine Referenz, also erstellen
				if( !tree[i]._ref )
				{
					impl_layer = this.__factory.createGroupLayer( tree[i] );
				}
				// Es existiert eine Referenz, also weiterverwenden
				else
				{
					// console.log(tree[i]._ref);

					// Wenn Gruppe halbtransparent ist, gibt es eine ol.Group-Instanz
					if( typeof tree[i]._ref.getImpl === 'function')
					{
						impl_layer = tree[i]._ref.getImpl();
					}
					// Ansonsten ist _ref eine ol.Layer-Liste, dessen erste Instanz alle anderen Instanzen referenziert
					else if( tree[i]._ref.length > 0 )
					{
						impl_layer = tree[i]._ref[0].getImpl();
					}
					// sollte nicht passieren
					else
					{
						gc.gis.iface.log.error( this, new gc.gis.error.GCError('Unexpected layer reference for: '+ tree[i]._ref ) );
					}
				}
			}
		}
		// console.log('GCLayerTreeManager.__rebuildMapLayers():   created:');
		// console.log( impl_layer );

		// Zur Openlayer-Karte hinzufügen
		if( impl_layer )
		{
			this._map.getMap().addLayer( impl_layer );
		}
	}
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__rebuildMapLayers(): Leaving ... ');
};

/**
 * Prüft, ob zwischen zwei Layerbäumen Unterschiede bestehen und markiert diese
 * ggf. mit einem Flag <code>__changed</code>.
 *
 * @private
 * @param {gc.gis.map.GCLayerTreeManager.TreeNode[]} target Zu vergleichender Baum
 * @param {gc.gis.map.GCLayerTreeManager.TreeNode[]} reference Baum, mit dem verglichen werden soll
 * @return {boolean}
 */
gc.gis.map.GCLayerTreeManager.prototype.__treeHasChanged = function( target, reference )
{
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__treeHasChanged()');

	var changed = false,
		i, ii,
		j, jj;

	if (!reference || target.length !== reference.length)
	{
		changed = true;
		if (target.length > 0)
		{
			for (i = 0, ii = target.length; i < ii; i++)
			{
				target[i].__changed = true;
			}
		}
	}
	else
	{
		for (i = 0, ii = target.length; i < ii; i++)	// the group holder
		{
			if (target[i].layers && reference[i].layers)
			{
				if (target[i].layers.length !== reference[i].layers.length)
				{
					target[i].__changed = true;
					changed = true;
				}
				else if (target[i].layers.length > 0 && reference[i].layers.length > 0)
				{
					for (j = 0, jj = target[i].layers.length; j < jj; j++)	// the groups' contents
					{
						// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__treeHasChanged(): '+ target[i].layers[j].getLabel() +' <> '+ reference[i].layers[j].getLabel() );
						if (target[i].layers[j] !== reference[i].layers[j])
						{
							target[i].__changed = true;	// Kennzeichnung, dass sich der Unterbaum geändert hat
							target[i].layers[j].__changed = true;
							changed = true;
							break;
						}
					}
				}
			}
			else if (target[i].groups && reference[i].groups)	// group layers
			{
				var match = this.__treeHasChanged(target[i].groups, reference[i].groups);
				if (match === true)
				{
					target[i].__changed = true;
					changed = true;
				}
			}
			else	// mixed groups/layers
			{
				target[i].__changed = true;
				changed = true;
			}
		}
	}
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__treeHasChanged(): '+ changed );

	return changed;
};

/**
 * Möglichst flache Liste von zusammengehörigen GCLayern erstellen, die
 * zusammengefasst werden können.
 *
 * @private
 * @param {gc.gis.layer.GCAbstractLayer[]} gc_layers Zu gruppierende Layer
 * @return {gc.gis.map.GCLayerTreeManager.TreeNode[]}
 */
gc.gis.map.GCLayerTreeManager.prototype.__createLayerGroups = function( gc_layers, opt_level )
{
	var level = opt_level || 0;
	var indent = '';
	for( var i=0; i<level; i++ ) {indent+='    ';}
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__createLayerGroups():'+indent+' Entering ... layers='+gc_layers.length);

	var config = [];	// Liste aller Gruppen
	var group = {	// aktuelle Gruppe
		layers: []
	};
	var last_layer = undefined;	// zuletzt betrachteter Layer

	for( var i=0; i<gc_layers.length; i++ )
	{
		if( !gc_layers[i].isReallyVisible() )
		{
			// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': layer['+i+'] is invisible. skipping ... ');
			continue;
		}
		// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': layer['+i+'] is visible');

		if( gc_layers[i] instanceof gc.gis.layer.GCGroupLayer )
		{
			if( gc_layers[i].getOpacity() < 1 )
			{
				// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': found semi transparent group layer');
				if( group.layers.length > 0 )
				{
					// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': semi transparent group: saving old group ... ');
					config.push( group );
					// console.log(config);
					group = {
						layers: []
					};
				}
				// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': semi transparent group: creating new group ... ');
				var gl = this.__createGroupLayer( gc_layers[i], level+1 );
				config.push( gl );
				// console.log(config);
				last_layer = undefined;
			}
			else
			{
				// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': found opaque group layer');
				var sub_group = this.__createLayerGroups( gc_layers[i].getLayers(), level+1 );
				// console.log(sub_group);
				for( var j=0; j<sub_group.length; j++ )
				{
					// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': opaque group: checking subgroup ... ');
					// console.log( sub_group[j] );
					if( sub_group[j].layers && sub_group[j].layers.length > 0 )
					{
						if( last_layer && this.__canMergeLayer( sub_group[j].layers[0], last_layer, indent ) )
						{
							// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': opaque group: merging subgroup to existing group ... ');
							// console.log(group);
							group.layers = group.layers.concat( sub_group[j].layers );
							// console.log(group);
						}
						else
						{
							if( group.layers.length > 0 )
							{
								// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': opaque group: saving old group ... ');
								config.push( group );
								// console.log(config);
							}
							// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': opaque group: creating new group ... ');
							group = {
								layers: sub_group[j].layers
							};
							last_layer = group.layers[group.layers.length-1];
						}
					}
					else if( sub_group[j].groups )
					{
						if( group.layers.length > 0 )
						{
							// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': opaque group: saving old group ... ');
							config.push( group );
							group = {
								layers: []
							};
							last_layer = undefined;
						}
						// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': opaque group: adding (sub)group layer ... ');
						config.push( sub_group[j] );
					}
				}
			}
		}
		else
		{
			// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': found regular layer   (group.length='+ group.length +'  last_layer='+last_layer+')');
			if( group.layers.length === 0 )	// assume 'group' is empty
			{
				// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': assuming unset last_layer');
				group = {
					layers: [gc_layers[i]]
				};
			}
			else
			{
				// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': comparing '+ gc_layers[i] +' __AND__ '+ last_layer );
				if( this.__canMergeLayer( gc_layers[i], last_layer, indent ) )
				{
					// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': adding to existing group ... ');
					group.layers.push( gc_layers[i] );
				}
				else	// neue Gruppe erstellen, alte merken
				{
					if( group.layers.length > 0 )
					{
						// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': saving old group ... ');
						config.push( group );
						// console.log(config);
					}
					// console.log('GCLayerTreeManager.__createLayerGroups():'+indent+' '+ gc_layers[i].getLabel() +': creating new group ... ');
					group = {
						layers: [gc_layers[i]]
					};
				}
			}
			last_layer = gc_layers[i];
		}
	}
	if( group.layers.length > 0 )
	{
		config.push( group );
		// console.log(config);
	}
	// console.log( config );
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__createLayerGroups():'+indent+' Leaving ... (groups created: '+ config.length +' out of layers: '+ gc_layers.length +')');

	return config;
};

/**
 * Aus einem GCGruppenLayer ein Objekt mit Layer-Gruppen machen
 *
 * @private
 * @param {gc.gis.layer.GCGroupLayer} gc_group
 * @return {gc.gis.map.GCLayerTreeManager.GroupNode}
 */
gc.gis.map.GCLayerTreeManager.prototype.__createGroupLayer = function( gc_group, level )
{
	var indent = '';
	for( var i=0; i<level; i++ ) {indent+='    ';}
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__createGroupLayer():'+indent+' "'+gc_group.getLabel()+'"');

	return {
		_ref:    gc_group,
		opacity: gc_group.getOpacity(),
		groups:  this.__createLayerGroups( gc_group.getLayers(), level )
	};
};

/**
 * Prüft, ob zwei Layer zu einem zusammengefasst werden können.
 *
 * @private
 * @param {gc.gis.layer.GCAbstractLayer} first
 * @param {gc.gis.layer.GCAbstractLayer} second
 * @return {boolean} <code>true</code> bei zusammenpassenden Rasterlayern, sonst <code>false</code>
 */
gc.gis.map.GCLayerTreeManager.prototype.__canMergeLayer = function( first, second, indent )
{
	// gc.gis.iface.log.debug( this, 'GCLayerTreeManager.__canMergeLayer()    :     '+indent+'"'+first.getLabel()+'" <> "'+ second.getLabel() +'" ?');

	var match = false;
	if (!(second instanceof gc.gis.layer.GCRasterLayer)
		|| second.getOpacity() < 1
		|| second.hasExternalConnectionSettings()
		|| !(first instanceof gc.gis.layer.GCRasterLayer)
		|| first.getOpacity() < 1
        || second.hasExternalConnectionSettings())
	{
        match = false;
	}
	else
	{
		// weitere Tests
        var match = !first.isCached() && !second.isCached()
				 && (first.isInternal() === second.isInternal());

		if( match )
		{
			match = match
				 && first.getUrl()
				 && second.getUrl()
				 && second.getUrl() === first.getUrl();

			if( match )
			{
				match = match
					 && (second instanceof gc.gis.layer.GCRasterLayer);

				if( match )
				{
					match = match
						 && (first.isTiled() === second.isTiled());
				}
			}
		}
	}

    // gc.gis.iface.log.debug(this, 'GCLayerTreeManager.__canMergeLayer()    :    ' + indent + ' ' + match );
    return match;
};

